import type { ShipTime } from '@felix/domain';
import { Button, Skeleton, Tabs } from '@mantine/core';
import {
  IconArrowBackUp,
  IconBuilding,
  IconCheck,
  IconClock,
  IconFileInvoice,
  IconFileSearch,
  IconPencil,
  IconSend,
  IconUser,
  IconX,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import { errorCode } from '../personnel/source.js';
import { ReturnedBanner } from '../returned/Returned.js';
import { ApprovedListTable } from './ApprovedListTable.js';
import { PriorityBadge, RequisitionStatusBadge } from './badges.js';
import { CreateRfqDialog } from './CreateRfqDialog.js';
import { FulfilmentTable } from './FulfilmentTable.js';
import { OfficeReviewPanel } from './OfficeReviewPanel.js';
import { OrdersBySupplier } from './OrdersBySupplier.js';
import { pricesVisible } from './orders.js';
import { PasswordDialog } from './PasswordDialog.js';
import classes from './Purchasing.module.css';
import { historyOf, RequisitionHistory } from './RequisitionHistory.js';
import {
  approvalComments,
  approvalNotes,
  formatAmount,
  formatQuantity,
  formatStampLocal,
  type WorkflowNode,
  workflowNodes,
} from './requisitions.js';
import type { PurchasingSource } from './source.js';
import { LoadError } from './table.js';
import type { RequisitionDetail } from './types.js';

type Dialog = 'submit' | 'approve' | 'return' | 'cancel' | 'rfq' | null;

/**
 * Talebin kartı ve onay akışı (`satin-alma-akisi.jpeg` "Approval Workflow"): adım şeridi
 * (Requested → kademenin adımları → PO Issued) ve onay yorumları; altta talebin bilgileri ve
 * kalemleri. Gemide gemideki adım, ofiste ofisteki adım onaylanır; karar parolayla (§7). Ofis
 * adımı gemide "ofis onayı bekliyor" görünür.
 */
export function RequisitionDetailView({
  source,
  id,
  message,
  onEdit,
  onBack,
  timeZone,
  extraActions,
  onOpenRfq,
  onOpenOrder,
  onShortfall,
}: {
  source: PurchasingSource;
  id: string;
  /** Önceki işlemin bildirimi (ör. "gönderildi"). */
  message?: string | null;
  onEdit: () => void;
  onBack: () => void;
  /** Tarih-saatin gösterildiği saat dilimi (gemide geminin). */
  /** Gemi saati: dilim ya da saat kaydının çizelgesi (ADR 0065 §4). */
  timeZone?: ShipTime;
  /** Ofiste "Create RFQ" gibi ek eylemler (M12b Task 14). */
  extraActions?: (detail: RequisitionDetail) => ReactNode;
  /** Ofiste talebin RFQ'sunu açar (M12b Task 14). */
  onOpenRfq?: (rfqId: string) => void;
  /** Talebin siparişi (ADR 0040 #188): ofiste ve gemide salt okunur kart. */
  onOpenOrder?: (purchaseOrderId: string) => void;
  /** Gemide eksik için yeni talep formu (ADR 0047 §6). */
  onShortfall?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`purchasing.workflow.${key}`, options);
  const [detail, setDetail] = useState<RequisitionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(message ?? null);
  const [dialog, setDialog] = useState<Dialog>(null);
  // Kalem sekmeleri; ofisin onaylı listesi varsa o açılır (ADR 0111 §A: gemi listeye göre hareket
  // eder)
  const [tab, setTab] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setDetail(await source.requisition(id));
    } catch (failure) {
      setError(errorCode(failure));
    }
  }, [source, id]);
  useEffect(() => {
    void load();
  }, [load]);

  const crumbTitle = detail?.requisitionNo ?? '';
  if (error) {
    return (
      <div className={classes.screen}>
        <PageHeader title={k('title')} crumb={crumbTitle} />
        <LoadError title={k('error')} code={error} onRetry={() => void load()} />
      </div>
    );
  }
  if (!detail) {
    return (
      <div className={classes.screen} aria-busy>
        <span className={classes.visuallyHidden}>{k('loading')}</span>
        <PageHeader title={k('title')} />
        {/* Düzenin şeklinde iskelet: şerit, iki panel ve kalemler (M6f §0.6) */}
        <Skeleton height={120} />
        <div className={classes.detailGrid}>
          <Skeleton height={220} />
          <Skeleton height={220} />
        </div>
        <Skeleton height={200} />
      </div>
    );
  }

  const stamp = (at: string | null) =>
    formatStampLocal(at, timeZone ?? detail.clock ?? detail.timeZone);
  const nodes = workflowNodes(detail);
  const history = historyOf(detail);
  const comments = approvalComments(detail);
  // Onay durumu (ADR 0091): kim bekliyor, karar nerede, kendi kaydı neden ofise gitti
  const notes = approvalNotes(detail, source.scope === 'ship' ? 'ship' : 'office');
  const decide = async (
    decision: 'approved' | 'returned',
    reason: string,
    password: string | undefined,
  ) => {
    // Karar ekranda görülen gönderim ve adım için verilir (bayat karar yazılmaz)
    if (!detail.pending) return 'NOT_PENDING';
    try {
      const next = await source.decide(detail.id, {
        contentHash: detail.contentHash,
        submissionId: detail.pending.submissionId,
        step: detail.pending.step,
        decision,
        ...(reason ? { reason } : {}),
        ...(password ? { password } : {}),
      });
      setDetail(next);
      setDialog(null);
      setNotice(k(decision === 'approved' ? 'approved' : 'returned', { no: next.requisitionNo }));
      return null;
    } catch (failure) {
      return errorCode(failure);
    }
  };
  const editor = source.editor;
  // Birincil eylemler: geniş ekranda başlıkta, küçük ekranda altta sabit çubukta (Responsive
  // kurallar)
  const primary: {
    key: string;
    label: string;
    props: { variant?: string; leftSection: ReactNode; onClick: () => void };
  }[] = [
    ...(detail.can.submit && editor
      ? [
          {
            key: 'submit',
            label: k('submit'),
            props: { leftSection: <IconSend size={16} />, onClick: () => setDialog('submit') },
          },
        ]
      : []),
    ...(detail.can.decide
      ? [
          {
            key: 'return',
            label: k('return'),
            props: {
              variant: 'default',
              leftSection: <IconArrowBackUp size={16} />,
              onClick: () => setDialog('return'),
            },
          },
          {
            key: 'approve',
            label: k('approve'),
            props: { leftSection: <IconCheck size={16} />, onClick: () => setDialog('approve') },
          },
        ]
      : []),
  ];

  return (
    <div className={classes.screen}>
      <PageHeader
        title={k('title')}
        description={k('description')}
        crumb={detail.requisitionNo}
        actions={
          <div className={classes.headerActions}>
            {detail.can.edit && editor ? (
              <Button variant="default" leftSection={<IconPencil size={16} />} onClick={onEdit}>
                {k('edit')}
              </Button>
            ) : null}
            {detail.can.cancel && editor ? (
              <Button
                variant="default"
                leftSection={<IconX size={16} />}
                onClick={() => setDialog('cancel')}
              >
                {k('cancelRequisition')}
              </Button>
            ) : null}
            {primary.map((action) => (
              <Button key={action.key} data-primary {...action.props}>
                {action.label}
              </Button>
            ))}
            {source.rfqs && detail.rfq && onOpenRfq ? (
              <Button
                variant="default"
                leftSection={<IconFileSearch size={16} />}
                onClick={() => onOpenRfq(detail.rfq?.id ?? '')}
              >
                {k('openRfq', { no: detail.rfq.rfqNo })}
              </Button>
            ) : null}
            {detail.order && onOpenOrder ? (
              <Button
                variant="default"
                leftSection={<IconFileInvoice size={16} />}
                onClick={() => onOpenOrder(detail.order?.id ?? '')}
              >
                {k('openOrder', { no: detail.order.poNo })}
              </Button>
            ) : null}
            {source.rfqs && detail.can.rfq ? (
              <Button leftSection={<IconSend size={16} />} onClick={() => setDialog('rfq')}>
                {k('createRfq')}
              </Button>
            ) : null}
            {extraActions?.(detail)}
          </div>
        }
      />

      {notice ? (
        <p className={classes.notice} data-tone="success" role="status">
          {notice}
        </p>
      ) : null}
      {/* İade bandı (ADR 0116 Ek): iade eden (gemide ya da ofiste), zaman, gerekçe */}
      {detail.status === 'returned' ? (
        <ReturnedBanner notice={detail.returnNotice} hint={Boolean(detail.can.edit && editor)} />
      ) : null}
      {notes.length > 0 ? (
        <section
          className={classes.notice}
          data-tone="info"
          aria-label={k('approvalStatus')}
          data-testid="approval-status"
        >
          {notes.map((note) => (
            <p key={note.key} className={classes.noteLine}>
              {k(`notes.${note.key}`, note.params)}
            </p>
          ))}
        </section>
      ) : null}
      {detail.cancelledAt ? (
        <p className={classes.notice} data-tone="danger">
          {k('cancelled', { reason: detail.cancelReason ?? '' })}
        </p>
      ) : null}

      <Panel aria-label={k('strip')}>
        <ol className={classes.strip} aria-label={k('strip')}>
          {nodes.map((node) => (
            <WorkflowStep key={node.key} node={node} stamp={stamp} />
          ))}
        </ol>
      </Panel>

      <div className={classes.detailGrid}>
        <Panel title={k('comments')} flush>
          {comments.length === 0 ? (
            <EmptyState title={k('noComments')} />
          ) : (
            <ul className={classes.comments}>
              {comments.map((comment) => (
                <li
                  key={`${comment.revision}-${comment.kind}-${comment.step}`}
                  className={classes.comment}
                >
                  <span className={classes.avatar} aria-hidden>
                    {comment.office ? <IconBuilding size={16} /> : <IconUser size={16} />}
                  </span>
                  <div>
                    <p className={classes.commentRole}>
                      {comment.role ?? comment.byName}
                      {comment.kind === 'returned' ? ` · ${k('labels.returned')}` : ''}
                    </p>
                    <p className={classes.commentText}>{comment.reason ?? k('noComment')}</p>
                  </div>
                  <span className={classes.commentAt}>{stamp(comment.at)}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title={k('details')} flush extra={<RequisitionStatusBadge status={detail.status} />}>
          <dl className={classes.facts}>
            <div>
              <dt>{t('purchasing.list.columns.requisitionNo')}</dt>
              <dd>{detail.requisitionNo}</dd>
            </div>
            <div>
              <dt>{t('purchasing.form.vessel')}</dt>
              <dd>{detail.vesselName}</dd>
            </div>
            <div>
              <dt>{t('purchasing.form.department')}</dt>
              <dd>
                {t(`purchasing.departments.${detail.department}`, {
                  defaultValue: detail.department,
                })}
              </dd>
            </div>
            <div>
              <dt>{t('purchasing.form.requestedBy')}</dt>
              <dd>
                {detail.requestedBy.name}
                {detail.requestedBy.role ? ` · ${detail.requestedBy.role}` : ''}
              </dd>
            </div>
            <div>
              <dt>{k('requestDate')}</dt>
              <dd>{formatDay(detail.requestDate)}</dd>
            </div>
            <div>
              <dt>{t('purchasing.form.requiredDate')}</dt>
              <dd>{formatDay(detail.requiredDate)}</dd>
            </div>
            <div>
              <dt>{t('purchasing.form.port')}</dt>
              <dd>{detail.port ?? '—'}</dd>
            </div>
            <div>
              <dt>{k('total')}</dt>
              <dd>
                {formatAmount(detail.total.priced, locale)} {detail.currency}
                {detail.total.value === null ? '*' : ''}
              </dd>
            </div>
            <div>
              <dt>{t('purchasing.form.remarks')}</dt>
              <dd>{detail.remarks ?? '—'}</dd>
            </div>
          </dl>
        </Panel>
      </div>

      <Panel flush aria-label={k('items')}>
        {/* Kalemler ve talep–temin karşılaştırması (ortağın notu #12; ADR 0047 §6) */}
        <Tabs
          value={tab ?? (detail.revision ? 'approved' : 'items')}
          onChange={setTab}
          className={classes.tabs}
          keepMounted={false}
        >
          <Tabs.List aria-label={k('itemTabs')}>
            <Tabs.Tab value="items">{k('items')}</Tabs.Tab>
            {/* Ofisin incelemesi ve onaylı liste (ADR 0111 §A) */}
            {source.revise && detail.can.revise ? (
              <Tabs.Tab value="review">{t('purchasing.review.officeTab')}</Tabs.Tab>
            ) : null}
            {detail.revision ? (
              <Tabs.Tab value="approved">
                {t('purchasing.review.approvedTab')} ·{' '}
                {t('purchasing.history.revisionBadge', { no: detail.revision.revisionNo })}
              </Tabs.Tab>
            ) : null}
            <Tabs.Tab value="fulfilment">{t('purchasing.fulfilment.title')}</Tabs.Tab>
            {/* Bölünmüş liste: siparişler tedarikçiye göre (ADR 0115) */}
            {detail.fulfilment?.hasOrders ? (
              <Tabs.Tab value="bySupplier">{t('purchasingShip.bySupplier.tab')}</Tabs.Tab>
            ) : null}
            {/* Tek zaman çizelgesi (D3/D6; ADR 0167) */}
            <Tabs.Tab value="history">
              {t('purchasing.history.tab', { count: history.length })}
            </Tabs.Tab>
          </Tabs.List>
          {source.revise && detail.can.revise ? (
            <Tabs.Panel value="review" className={classes.tabBody}>
              <OfficeReviewPanel
                key={`${detail.id}-${detail.revision?.revisionNo ?? 0}`}
                detail={detail}
                source={source}
                onSent={(next) => {
                  setDetail(next);
                  setTab('approved');
                  setNotice(t('purchasing.review.sent', { no: next.revision?.revisionNo ?? '' }));
                }}
              />
            </Tabs.Panel>
          ) : null}
          {detail.revision ? (
            <Tabs.Panel value="approved" className={classes.tabBody}>
              <ApprovedListTable detail={detail} stamp={stamp} />
            </Tabs.Panel>
          ) : null}
          <Tabs.Panel value="items" className={classes.tabBody}>
            <section
              className={table.tableWrap}
              aria-label={k('items')}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
              tabIndex={0}
            >
              <table className={`${table.table} ${classes.table}`}>
                <thead>
                  <tr>
                    <th scope="col" className={classes.index}>
                      {t('purchasing.form.columns.no')}
                    </th>
                    <th scope="col" className={classes.key}>
                      {t('purchasing.form.columns.material')}
                    </th>
                    <th scope="col">{t('purchasing.form.columns.description')}</th>
                    <th scope="col" data-numeric>
                      {t('purchasing.form.columns.qty')}
                    </th>
                    <th scope="col">{t('purchasing.form.columns.uom')}</th>
                    <th scope="col" data-numeric>
                      {t('purchasing.review.columns.inStock')}
                    </th>
                    <th scope="col" data-numeric>
                      {t('purchasing.form.columns.unitPrice', { currency: detail.currency })}
                    </th>
                    <th scope="col" data-numeric>
                      {t('purchasing.form.columns.total', { currency: detail.currency })}
                    </th>
                    <th scope="col">{t('purchasing.form.columns.priority')}</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.lines.map((line) => (
                    <tr key={line.id}>
                      <td className={classes.index}>{line.lineNo}</td>
                      <td className={`${classes.material} ${classes.key}`}>{line.name}</td>
                      <td className={classes.muted}>{line.description ?? '—'}</td>
                      <td data-numeric>{formatQuantity(line.quantity, locale)}</td>
                      <td>{line.unit}</td>
                      {/* Stok mevcudu (ADR 0111 §A): ofisin de önüne düşer */}
                      <td data-numeric data-tone={(line.onHand ?? 0) > 0 ? 'info' : undefined}>
                        {line.onHand === undefined || line.onHand === null
                          ? '—'
                          : formatQuantity(line.onHand, locale)}
                      </td>
                      <td data-numeric>{formatAmount(line.unitPrice, locale)}</td>
                      <td data-numeric>{formatAmount(line.total, locale)}</td>
                      <td>
                        <PriorityBadge priority={line.priority} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          </Tabs.Panel>
          <Tabs.Panel value="fulfilment" className={classes.tabBody}>
            <FulfilmentTable
              fulfilment={detail.fulfilment}
              onShortfall={editor && detail.can.shortfall && onShortfall ? onShortfall : null}
              prices={pricesVisible(source.scope)}
            />
          </Tabs.Panel>
          {detail.fulfilment?.hasOrders ? (
            <Tabs.Panel value="bySupplier" className={classes.tabBody}>
              <OrdersBySupplier
                fulfilment={detail.fulfilment}
                prices={pricesVisible(source.scope)}
              />
            </Tabs.Panel>
          ) : null}
          <Tabs.Panel value="history" className={classes.tabBody}>
            <RequisitionHistory detail={detail} stamp={stamp} ship={source.scope === 'ship'} />
          </Tabs.Panel>
        </Tabs>
      </Panel>

      <div>
        <Button variant="subtle" onClick={onBack}>
          {k('back')}
        </Button>
      </div>

      {primary.length > 0 ? (
        <div
          className={classes.mobileActions}
          role="toolbar"
          aria-label={t('purchasing.table.actions')}
        >
          {primary.map((action) => (
            <Button key={action.key} {...action.props}>
              {action.label}
            </Button>
          ))}
        </div>
      ) : null}

      <PasswordDialog
        opened={dialog === 'approve'}
        title={t('purchasing.password.approveTitle')}
        body={t('purchasing.password.approveBody', { no: detail.requisitionNo })}
        reason="optional"
        reasonLabel={t('purchasing.password.comment')}
        password
        reauthKind="requisition"
        confirmLabel={k('approve')}
        onClose={() => setDialog(null)}
        onConfirm={({ reason, password }) => decide('approved', reason, password)}
      />
      <PasswordDialog
        opened={dialog === 'return'}
        title={t('purchasing.password.returnTitle')}
        body={t('purchasing.password.returnBody', { no: detail.requisitionNo })}
        reason="required"
        password
        reauthKind="requisition"
        confirmLabel={k('return')}
        onClose={() => setDialog(null)}
        onConfirm={({ reason, password }) => decide('returned', reason, password)}
      />
      {dialog === 'rfq' && source.rfqs ? (
        <CreateRfqDialog
          requisition={detail}
          today={new Date().toISOString().slice(0, 10)}
          source={source.rfqs}
          onClose={() => setDialog(null)}
          onCreated={(rfq) => {
            setDialog(null);
            onOpenRfq?.(rfq.id);
          }}
        />
      ) : null}
      {editor ? (
        <>
          <PasswordDialog
            opened={dialog === 'submit'}
            title={t('purchasing.password.submitTitle')}
            body={t('purchasing.password.submitBody')}
            reason="none"
            password
            reauthKind="requisition"
            confirmLabel={k('submit')}
            onClose={() => setDialog(null)}
            onConfirm={async ({ password }) => {
              try {
                const next = await editor.submit(detail.id, {
                  contentHash: detail.contentHash,
                  ...(password ? { password } : {}),
                });
                setDetail(next);
                setDialog(null);
                setNotice(t('purchasing.form.submitted', { no: next.requisitionNo }));
                return null;
              } catch (failure) {
                return errorCode(failure);
              }
            }}
          />
          <PasswordDialog
            opened={dialog === 'cancel'}
            title={t('purchasing.password.cancelTitle')}
            body={t('purchasing.password.cancelBody')}
            reason="required"
            password={false}
            confirmLabel={k('cancelRequisition')}
            onClose={() => setDialog(null)}
            onConfirm={async ({ reason }) => {
              try {
                const next = await editor.cancel(detail.id, {
                  rowVersion: detail.rowVersion,
                  reason,
                });
                setDetail(next);
                setDialog(null);
                setNotice(k('cancelledDone', { no: next.requisitionNo }));
                return null;
              } catch (failure) {
                return errorCode(failure);
              }
            }}
          />
        </>
      ) : null}
    </div>
  );
}

function WorkflowStep({
  node,
  stamp,
}: {
  node: WorkflowNode;
  stamp: (at: string | null) => string;
}) {
  const { t } = useTranslation();
  const icon =
    node.state === 'done' ? (
      <IconCheck size={20} stroke={3} />
    ) : node.state === 'returned' ? (
      <IconArrowBackUp size={18} />
    ) : node.key === 'po' ? (
      <IconFileInvoice size={18} />
    ) : (
      <IconClock size={18} />
    );
  return (
    <li className={classes.node} data-state={node.state}>
      <span className={classes.dot} aria-hidden>
        {icon}
      </span>
      <div className={classes.nodeCard}>
        <span className={classes.nodeLabel}>{t(`purchasing.workflow.labels.${node.label}`)}</span>
        <span className={classes.nodeRole}>{node.role ?? '-'}</span>
        <span className={classes.nodeAt}>
          {node.at ? stamp(node.at) : node.key === 'po' ? '-' : ''}
        </span>
        {node.office && node.key !== 'po' ? (
          <span className={classes.officeTag}>
            <StatusBadge tone="info">{t('purchasing.workflow.office')}</StatusBadge>
          </span>
        ) : null}
      </div>
    </li>
  );
}
